import { createEffect, createSignal, onCleanup } from 'solid-js';
import { useVenue } from './VenueProvider';

/**
 * How the console answers a press, in two beats.
 *
 * Beat one belongs to the control: it changes state in the same frame as the press, before anything
 * is sent anywhere, so a tap is never answered by a screen that looks exactly as it did. Beat two is
 * the spinner — the system saying it is still working — and it resolves into the confirmed state when
 * the controller acks, or lets go when it refuses and the feedback slot says why.
 *
 * Both beats live here rather than in `VenueProvider`, which already owns the action keys themselves.
 */
export interface PressFeedback {
  /** Beat one: held down, from the click until the action it started settles. */
  pressed: () => boolean;
  /** Call synchronously from the click handler, before dispatching. */
  press: () => void;
}

export function usePressFeedback(busy: () => boolean): PressFeedback {
  const [pressed, setPressed] = createSignal(false);
  let sawBusy = false;
  let release: ReturnType<typeof globalThis.setTimeout> | undefined;

  createEffect(() => {
    if (busy()) {
      sawBusy = true;
      return;
    }
    if (sawBusy) {
      sawBusy = false;
      setPressed(false);
    }
  });

  onCleanup(() => {
    if (release != null) globalThis.clearTimeout(release);
  });

  return {
    pressed,
    press(): void {
      setPressed(true);
      if (release != null) globalThis.clearTimeout(release);
      // A press that dispatched nothing — a guard that returned early, a confirmation that took over
      // the screen — must not leave the control held down for the rest of the session.
      release = globalThis.setTimeout(() => {
        if (!busy()) setPressed(false);
      }, 0);
    },
  };
}

/**
 * The same two beats for a dragged control.
 *
 * A slider's value moves under the finger immediately — the screens keep their own preview for that —
 * and only the COMMIT is in doubt. `controllerGesture` already coalesces the drag and calls back once
 * per committed gesture; this wraps those callbacks so the row that was committed can show the
 * spinner until the controller has answered for it.
 */
export interface GestureCommits {
  /** Is this control's last committed gesture still unanswered? */
  committing: (key: string) => boolean;
  /** `controllerGesture`, with the commit tracked. */
  commit: (
    name: string,
    params: Record<string, unknown>,
    key: string,
    final: boolean,
    onApplied?: (result: unknown) => void,
    onRejected?: (error: unknown) => void,
  ) => void;
}

export function useGestureCommits(): GestureCommits {
  const venue = useVenue();
  const [inFlight, setInFlight] = createSignal<ReadonlySet<string>>(new Set());

  function mark(key: string, active: boolean): void {
    setInFlight((current) => {
      if (current.has(key) === active) return current;
      const next = new Set(current);
      if (active) next.add(key);
      else next.delete(key);
      return next;
    });
  }

  return {
    committing: (key: string) => inFlight().has(key),
    commit(name, params, key, final, onApplied, onRejected): void {
      if (final) mark(key, true);
      venue.controllerGesture(
        name,
        params,
        key,
        final,
        final
          ? (result) => {
            mark(key, false);
            onApplied?.(result);
          }
          : onApplied,
        final
          ? (error) => {
            mark(key, false);
            onRejected?.(error);
          }
          : onRejected,
      );
    },
  };
}
